import { array, check, literal, minLength, object, pipe, string, variant } from "valibot";
import { isEmpty, isNil } from "lodash-es";
import { MAX_CHARACTERS_SM } from "../../constants";
import {
  fieldValueReferenceOfTypes,
  metaVariableValueReferenceOfTypes,
  variableValueReferenceOfTypes,
} from "./references";
import { email, errorTranslation } from "./base";
import { keyFromSelector } from "i18next";
import { stringTemplate } from "./stringTemplate";
import type { Delimiter, StringTemplate } from "../stringTemplateUtil";
import { REPLY_TO_ALLOWED_PLACEHOLDER_FIELDS } from "../fieldUtil";
import { isValidEmail } from "../emailUtil";

export const recipient = () =>
  variant("type", [
    object({
      type: literal("literal"),
      value: email(MAX_CHARACTERS_SM),
    }),
    object({
      type: literal("field"),
      value: fieldValueReferenceOfTypes(["email", "lookup", "radio", "search"]),
    }),
    object({
      type: literal("variable"),
      value: variableValueReferenceOfTypes(["string"]),
    }),
    object({
      type: literal("metaVariable"),
      value: metaVariableValueReferenceOfTypes(["sys.user"]),
    }),
    object({
      type: literal("unknown"),
      value: string(),
    }),
  ]);

export const requireRecipients = () =>
  pipe(array(recipient()), minLength(1, errorTranslation(keyFromSelector(($) => $.VALIDATION_RECIPIENTS_MIN))));

// Only a fully typed address can be checked, a placeholder is resolved on submission
const literalAddress = (template: StringTemplate) =>
  template.every(({ type }) => type === "literal") ? template.map(({ value }) => value).join("") : undefined;

export const replyTo = (delimiter: Delimiter) =>
  pipe(
    stringTemplate(REPLY_TO_ALLOWED_PLACEHOLDER_FIELDS, [], [], MAX_CHARACTERS_SM, false, delimiter),
    // A placeholder combined with text or another placeholder can't resolve to a single address
    check(
      (template) => !isNil(literalAddress(template)) || template.length === 1,
      errorTranslation(keyFromSelector(($) => $.VALIDATION_REPLY_TO_SINGLE_ADDRESS)),
    ),
    check(
      (template) => {
        const address = literalAddress(template);
        return isNil(address) || isEmpty(address) || isValidEmail(address);
      },
      // The message shows the input, which would otherwise be the template instead of the address
      (issue) =>
        errorTranslation(
          keyFromSelector(($) => $.VALIDATION_EMAIL),
          { input: literalAddress(issue.input) },
        )(issue),
    ),
  );
